import { Car, ChevronRight, LogOut } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchCompanyById, getCompanyById, INVENTORY_COMPANIES_LS_KEY } from "@/lib/admin/companiesStore";
import { getStoredAuthUser } from "@/lib/authUser";
import { logout } from "@/services/authService";
import { dashboardNavItem } from "@/lib/appNavConfig";
import { findActiveNavModule, firstLeaf, useAppNavModules } from "@/hooks/useAppNavModules";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function AppSidebar() {
  const { state, isMobile, setOpen, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const navModules = useAppNavModules();
  const activeModule = findActiveNavModule(location.pathname, navModules);
  /** At most one module accordion open; which id is expanded (null = all closed). */
  const [openModuleId, setOpenModuleId] = useState<string | null>(null);
  const lastAutoOpenedModuleId = useRef<string | null>(null);

  useEffect(() => {
    const id = activeModule?.id ?? null;
    if (id === null) {
      lastAutoOpenedModuleId.current = null;
      setOpenModuleId(null);
      return;
    }
    if (id !== lastAutoOpenedModuleId.current) {
      lastAutoOpenedModuleId.current = id;
      setOpenModuleId(id);
    }
  }, [activeModule?.id]);

  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  const [companyBrandingTick, setCompanyBrandingTick] = useState(0);
  const tenantCompanyId = authUser?.company_id && authUser.company_id > 0 ? authUser.company_id : null;

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setAuthUser(getStoredAuthUser());
    };
    const onAuth = () => setAuthUser(getStoredAuthUser());
    const onCompaniesCache = () => setCompanyBrandingTick((t) => t + 1);
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    window.addEventListener("inventory_companies_cache_changed", onCompaniesCache);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
      window.removeEventListener("inventory_companies_cache_changed", onCompaniesCache);
    };
  }, []);

  useEffect(() => {
    if (!tenantCompanyId) return;
    /** Logo + name come from GET `/api/v1/user` (`company_branding`) — no `company_can_view` required. */
    if (authUser?.company_branding?.logo?.trim()) {
      return;
    }
    const cached = getCompanyById(String(tenantCompanyId));
    if (cached?.logo) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await fetchCompanyById(String(tenantCompanyId));
        if (!cancelled) setCompanyBrandingTick((t) => t + 1);
      } catch {
        /* ignore — e.g. 403 without company_can_view; branding should still load from /user */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCompanyId, authUser?.company_branding?.logo]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === INVENTORY_COMPANIES_LS_KEY) setCompanyBrandingTick((t) => t + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const tenantCompany = useMemo(() => {
    return tenantCompanyId ? getCompanyById(String(tenantCompanyId)) : undefined;
  }, [tenantCompanyId, companyBrandingTick]);
  const tenantLogoUrl =
    authUser?.company_branding?.logo?.trim() ||
    (tenantCompany?.logo?.trim() ? tenantCompany.logo.trim() : null);
  const tenantName =
    authUser?.company_branding?.name?.trim() || tenantCompany?.name?.trim() || "";
  /** Custom tenant header: only when API has both logo URL and company name (no AutoMax product title). */
  const showTenantLogoOnly = Boolean(tenantLogoUrl && tenantName);

  /** Close mobile sheet after navigation; desktop sidebar stays expanded. */
  const afterNavSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("[Auth] Logout request failed", error);
    } finally {
      localStorage.removeItem("auth_user");
      navigate("/login");
    }
  };

  const brandBlock = showTenantLogoOnly ? (
    <div className="flex w-full min-w-0 items-center justify-center px-1">
      <img
        src={tenantLogoUrl!}
        alt={tenantName}
        className={cn(
          "max-w-full object-contain object-center",
          collapsed && !isMobile ? "max-h-9" : "max-h-11 sm:max-h-12",
        )}
      />
    </div>
  ) : (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-primary/20 bg-primary/10">
        {tenantLogoUrl ? (
          <img src={tenantLogoUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <Car className="h-5 w-5 text-primary" />
        )}
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <h1 className="truncate font-display text-base font-bold text-sidebar-accent-foreground">
            Auto<span className="text-primary">Max</span>
          </h1>
          <p className="text-[10px] text-sidebar-foreground">Inventory System</p>
        </div>
      )}
    </div>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border sidebar-glow">
      <div
        className={
          collapsed && !isMobile
            ? "px-2 py-3 border-b border-sidebar-border flex justify-center"
            : "p-4 border-b border-sidebar-border"
        }
      >
        {collapsed && !isMobile ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex cursor-default justify-center">{brandBlock}</div>
            </TooltipTrigger>
            <TooltipContent side="right" align="start" className="max-w-[220px]">
              {showTenantLogoOnly ? (
                <>
                  <p className="font-display text-sm font-semibold">{tenantName}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Inventory System</p>
                </>
              ) : (
                <>
                  <p className="font-display font-semibold">
                    Auto<span className="text-primary">Max</span>
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Inventory System</p>
                  {tenantName ? (
                    <p className="mt-1 border-t border-border/50 pt-1 text-xs text-muted-foreground">{tenantName}</p>
                  ) : null}
                </>
              )}
            </TooltipContent>
          </Tooltip>
        ) : (
          brandBlock
        )}
      </div>

      <SidebarContent className="px-2 pt-2 group-data-[collapsible=icon]:!px-0">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive(dashboardNavItem.url)}
                  tooltip={dashboardNavItem.title}
                  className={isActive(dashboardNavItem.url) ? "bg-sidebar-accent text-primary data-[active=true]:text-primary" : ""}
                >
                  <NavLink
                    to={dashboardNavItem.url}
                    onClick={afterNavSidebar}
                    className="hover:bg-sidebar-accent/50 rounded-lg transition-colors"
                    activeClassName="bg-sidebar-accent text-primary font-medium"
                  >
                    <dashboardNavItem.icon />
                    <span>{dashboardNavItem.title}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-3 group-data-[collapsible=icon]:gap-2">
              {navModules.map((mod) => {
                const activeHere = activeModule?.id === mod.id;
                const first = firstLeaf(mod.items);
                const ModIcon = mod.icon;
                const isIconRail = collapsed && !isMobile;

                if (isIconRail) {
                  return (
                    <SidebarMenuItem key={mod.id}>
                      <SidebarMenuButton
                        type="button"
                        tooltip={mod.title}
                        isActive={activeHere}
                        className={activeHere ? "bg-sidebar-accent text-primary data-[active=true]:text-primary" : ""}
                        onClick={() => {
                          if (first) navigate(first);
                          afterNavSidebar();
                        }}
                      >
                        <ModIcon />
                        <span>{mod.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem key={mod.id}>
                    <Collapsible
                      className="group/collapsible min-w-0 w-full"
                      open={openModuleId === mod.id}
                      onOpenChange={(open) => {
                        if (open) {
                          setOpenModuleId(mod.id);
                        } else {
                          setOpenModuleId((cur) => (cur === mod.id ? null : cur));
                        }
                      }}
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          type="button"
                          isActive={activeHere}
                          tooltip={mod.title}
                          className={cn(
                            activeHere && "bg-sidebar-accent text-primary data-[active=true]:text-primary",
                            "[&[data-state=open]]:bg-sidebar-accent/70",
                          )}
                        >
                          <ModIcon />
                          <span className="truncate">{mod.title}</span>
                          <ChevronRight className="ml-auto shrink-0 size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {mod.items.map((item) => {
                            const ItemIcon = item.icon;
                            if (item.children && item.children.length > 0) {
                              return (
                                <SidebarMenuSubItem key={`${item.title}-${item.url}`}>
                                  <div className="px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/70">
                                    {item.title}
                                  </div>
                                  <SidebarMenuSub className="mx-2 mb-1 border-l-0 px-0 py-0">
                                    {item.children.map((child) => {
                                      const ChildIcon = child.icon;
                                      return (
                                        <SidebarMenuSubItem key={child.url}>
                                          <SidebarMenuSubButton asChild isActive={isActive(child.url)} size="sm">
                                            <NavLink
                                              to={child.url}
                                              onClick={afterNavSidebar}
                                              className="no-underline"
                                              activeClassName="font-medium"
                                            >
                                              <ChildIcon />
                                              <span>{child.title}</span>
                                            </NavLink>
                                          </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                      );
                                    })}
                                  </SidebarMenuSub>
                                </SidebarMenuSubItem>
                              );
                            }
                            return (
                              <SidebarMenuSubItem key={item.url}>
                                <SidebarMenuSubButton asChild isActive={isActive(item.url)} size="sm">
                                  <NavLink
                                    to={item.url}
                                    onClick={afterNavSidebar}
                                    className="no-underline"
                                    activeClassName="font-medium"
                                  >
                                    <ItemIcon />
                                    <span>{item.title}</span>
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3 group-data-[collapsible=icon]:!p-2">
        {collapsed && !isMobile ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center justify-center gap-0 w-full px-2 py-2 rounded-lg text-sm text-sidebar-foreground hover:bg-destructive/10 hover:text-destructive transition-colors group-data-[collapsible=icon]:justify-center"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                <span className="sr-only">Sign Out</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              Sign Out
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-sidebar-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span>Sign Out</span>
          </button>
        )}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
